<script setup lang="ts">
import { nextTick, ref } from "vue";
import { locale } from "../../locale";
import SwitchItem from "../SwitchItem.vue";
import { roomState } from "../../states/room";
import { fetcher } from "../../api/fetcher";
import ExitModalBtn from "../ExitModalBtn.vue";
import TextArea from "../TextArea.vue";
import { viewState } from "../../states/viewState";
import InputBox from "../InputBox.vue";
import {
  combineValidators,
  getEmptyFieldValidator,
  getMaxLenValidator,
} from "../../utils/validators";
import BaseButton from "../BaseButton.vue";
import { isSelfAdmin } from "../../states/adminsState";

const permanent = ref(roomState.roomType === "Permanent");
const waitRoom = ref(roomState.waitRoom);
const name = ref(roomState.name);
const description = ref(roomState.description || "");

const nameValidator = combineValidators([
  getEmptyFieldValidator(locale.validators.fieldnames.meetname).validator,
  getMaxLenValidator(100, locale.validators.fieldnames.meetname).validator,
]);
const descValidator = getMaxLenValidator(
  1000,
  locale.validators.fieldnames.description,
);

const input = ref<typeof InputBox | null>(null);
const textarea = ref<typeof TextArea | null>(null);

const apply = async () => {
  name.value = name.value.trim();
  await new Promise((resolve) => {
    nextTick(() => resolve(null));
  });
  nameValidator.validator(name.value);
  if (!nameValidator.state.value) {
    input.value?.shake();
    return;
  }
  description.value = description.value.trim();
  await new Promise((resolve) => {
    nextTick(() => resolve(null));
  });
  descValidator.validator(description.value);
  if (!descValidator.state.value) {
    textarea.value?.shake();
    return;
  }
  await fetcher
    .patch({
      path: `/room/${roomState.id}`,
      payload: {
        roomType: permanent.value ? "Permanent" : "Once",
        waitRoom: waitRoom.value,
        name: name.value,
        description: description.value,
      },
    })
    .then(() => {
      viewState.detailDialog = false;
    })
    .catch(() => {
      permanent.value = roomState.roomType === "Permanent";
      waitRoom.value = roomState.waitRoom;
      name.value = roomState.name;
      description.value = roomState.description || "";
    });
};
const cancel = async () => {
  viewState.detailDialog = false;
};
</script>
<template>
  <div class="modal-area">
    <div class="detail-modal">
      <div class="title">
        <span class="text">{{ locale.detail }}</span>
        <ExitModalBtn @click="viewState.detailDialog = false" />
      </div>
      <InputBox ref="input" v-model="name" :validator="nameValidator.validator" :title="locale.name"
        :placeholder="locale.passname" :disabled="!isSelfAdmin" />
      <TextArea ref="textarea" :title="locale.description" v-model="description" :validator="descValidator.validator"
        :placeholder="locale.passdescription" :disabled="!isSelfAdmin" />
      <div v-if="isSelfAdmin" class="switch-wrapper">
        <SwitchItem v-model="permanent" :text="locale.permanentLinkSwitchText" />
      </div>
      <div v-if="isSelfAdmin" class="switch-wrapper">
        <SwitchItem v-model="waitRoom" :text="locale.WaitRoomSwitchText" />
      </div>
      <div v-if="isSelfAdmin" class="controls">
        <BaseButton class="btm-btn" color="green" @click="apply">{{ locale.apply }}</BaseButton>
        <BaseButton class="btm-btn" color="grey" @click="cancel">{{
          locale.cancel
          }}</BaseButton>
      </div>
    </div>
  </div>
</template>
<style scoped>
.modal-area {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  height: 100dvh;
  width: 100vw;
  z-index: 100;
}

.switch-wrapper {
  padding: 0 12px;
}

.detail-modal {
  width: 560px;
  border-radius: 10px;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.15);
  padding: 24px 12px 24px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #fff;
  position: relative;
  top: 11px;
}

.title {
  height: 24px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title .text {
  color: #292929;
  text-align: center;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
}

.controls {
  height: 44px;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  padding: 8px 12px 0 12px;
  flex-direction: row-reverse;
  gap: 24px;
  justify-content: space-between;
}

.btm-btn {
  width: 200px;
}
</style>
